import { useSettlementToken } from "features/tokens/useSettlementToken";
import React, { useEffect } from "react";
import Button from "components/button/Button";
import ErrorModal from "modals/error-modal/ErrorModal";
import SuccessModal from "modals/success-modal/SuccessModal";
import { useTopUpSigning, SIGNING_PHASE_LABEL } from "./useTopUpSigning";
import type { AccountWithBalance } from "./useAccountFetcher";
import type { Network } from "@gardio/contracts/polygon";
import "./signing-review.css";
import CardNumber from "components/card-number/CardNumber";
import { TopUpStep } from "./useTopUp";

interface Props {
  account: AccountWithBalance;
  cardNumber: string;
  cardAliasAddress: string;
  amount: string;
  network: Network;
  onBack: () => void;
  onClose: () => void;
  onSuccess: () => void;
  setPhase: (ph: TopUpStep) => void;
}

function SigningReview({
  account,
  cardNumber,
  cardAliasAddress,
  amount,
  network,
  onBack,
  onClose,
  onSuccess,
  setPhase
}: Props) {
  const { label: tokenSymbol } = useSettlementToken();
  const {
    signingPhase,
    showError,
    errorMsg,
    showSuccess,
    isBusy,
    blindSigning,
    handleSign,
    handleBroadcast,
    handleCloseError,
    handleSuccessClose,
    resetSigning,
  } = useTopUpSigning(network, amount, cardAliasAddress, setPhase, account );

  // Signing happens on the hardware wallet — kick it off as soon as the
  // review screen opens. The user's confirmation is the physical button press
  // on the Keyfob, not a software click.
  useEffect(() => {
    handleSign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isConfirmReady = signingPhase === "confirm_ready";
  const showStatusBanner = signingPhase !== "idle";

  const handleSuccessAndClose = () => {
    handleSuccessClose();
    onSuccess();
  };

  const handleBack = () => {
    resetSigning();
    onBack();
  };

  return (
    <>
      <div className="tu-review">
        <div className="tu-review-header">
          <h3 className="v1-modal-title">Approve Transaction</h3>
          <p className="v1-modal-subtitle">
            {blindSigning
              ? "Check the details HERE — the Keyfob cannot display them for this token."
              : "Check the details — your Keyfob device will ask you to confirm."}
          </p>
        </div>

        {/* The device shows nothing for a token it has no signed record for, so the details below
            are the only place they can be checked. Says so plainly rather than letting the
            operator assume the hardware verified them. */}
        {blindSigning && (
          <div className="tu-review-blind" role="alert">
            <strong>Signing blind.</strong> No signed device record exists for{" "}
            {tokenSymbol} on this network, so your Keyfob will show a plain
            transaction with no token, amount or recipient. Verify the details
            below before pressing confirm.
          </div>
        )}

        <div className="tu-review-rows">
          {/* <div className="tu-review-row">
            <span className="tu-review-row__label">From</span>
            <div className="tu-review-row__val-stack">
              <span className="tu-review-mono">{account.address}</span>
              <span className="tu-review-mono">{treasuryAddress}</span>
              <span className="tu-review-path">{account.path}</span>
            </div>
          </div> */}

          <div className="tu-review-row">
            <span className="tu-review-row__label">To (Card)</span>
            <div className="tu-review-row__val-stack">
              <span className="tu-review-val">
                <CardNumber value={cardNumber} />
              </span>
              {/* <span className="tu-review-mono tu-review-mono--dim">
                {cardAliasAddress}
              </span> */}
            </div>
          </div>

          <div className="tu-review-row tu-review-row--highlight">
            <span className="tu-review-row__label">Amount</span>
            <div className="tu-review-amount">
              {Number(amount).toFixed(2)}
              <span className="tu-review-amount__unit"> {tokenSymbol}</span>
            </div>
          </div>
        </div>

        {showStatusBanner && (
          <div
            className={[
              "tu-phase-banner",
              isConfirmReady
                ? "tu-phase-banner--ready"
                : "tu-phase-banner--active",
            ].join(" ")}
          >
            {isBusy && <span className="tu-spinner" />}
            {SIGNING_PHASE_LABEL[signingPhase]}
          </div>
        )}

        <div className="tu-review-actions">
          <Button
            variant="ghost"
            size="md"
            onClick={handleBack}
            disabled={isBusy}
          >
            Back
          </Button>

          {isConfirmReady && (
            <Button variant="success" size="md" onClick={handleBroadcast}>
              Continue
            </Button>
          )}
        </div>
      </div>

      <ErrorModal
        isOpen={showError}
        errorMsg={errorMsg}
        handleClose={() => {
          handleCloseError();
          onClose();
        }}
        tryAgain={handleSign}
      />

      <SuccessModal
        isOpen={showSuccess}
        message={`${amount} ${tokenSymbol} successfully sent to card ${cardNumber}!`}
        handleClose={handleSuccessAndClose}
      />
    </>
  );
}

export default SigningReview;
